// @flow
import React from 'react';
import Latex from './Latex.js';
import {
  types as t,
  usePropsValidation,
} from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';

import Figure from './Figure.js';

const semiBoldProximaNova = 600;

const TabularData = (props: {
  title?: string,
  rowTitles?: Array<string>,
  rowTitlesAs?: 'latex' | 'text',
  columnTitles?: Array<string>,
  columnTitlesAs?: 'latex' | 'text',
  rowData: Array<Array<string>>,
  rowDataAs: 'latex' | 'text',
  ...CompilerInjectedProps,
}) => {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    TabularData,
  );

  const {
    title,
    rowTitles,
    rowTitlesAs,
    columnTitles,
    columnTitlesAs,
    rowData,
    rowDataAs,
  } = props;
  const latexOrText = (as, content) => {
    // TODO use LatexInternal instead
    if (as === 'latex') return <Latex expression={content} />;
    if (as === 'text') return content;
    // TODO need validation so this never happens
    return null;
  };

  // Assertion based upon multiple prop values. This is because we are using
  // one prop to toggle the "mode" of another prop. We could try to encode
  // this together in the prop itself, but that might be clunkier for the authors...
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;

    // TODO these error messages look pretty lame (especially when >1 latex errors for a given list)
    function validateLatexFor(propName, propValue) {
      const errors = propValue
        .map((latexString) => {
          // We create an object to adhere to the "props" interface of checkers
          return t.latex({ latexString }, 'latexString');
        })
        .filter(Boolean);

      if (errors.length > 0) {
        addErrors([
          {
            message: `Invalid usage(s) of latex:\n - ${errors
              .map((e) => e.reason)
              .join('\n - ')}`,
            ...props._sourceLocations?.[propName],
          },
        ]);
      }
    }
    if (rowTitles && rowTitlesAs === 'latex')
      validateLatexFor('rowTitles', rowTitles);
    if (columnTitles && columnTitlesAs === 'latex')
      validateLatexFor('columnTitles', columnTitles);
    if (rowData && rowDataAs === 'latex')
      validateLatexFor('rowData', rowData.flat());

    // Ensure that every row is titled, and there aren't excess row titles.
    if (rowTitles && rowTitles.length !== rowData.length) {
      addErrors([
        {
          message: `The number of titles in rowTitles must match the number of rows in rowData`,
          ...props._sourceLocations?.rowTitles,
        },
      ]);
    }

    // Ensure that all rows have the same number of items (ie. columns)
    const rowLengths =
      rowData.length === 0 ? null : rowData.map((row) => row.length);
    if (
      rowLengths !== null &&
      Math.min(...rowLengths) !== Math.max(...rowLengths)
    ) {
      addErrors([
        {
          message: `All rows must have the same number of items.`,
          ...props._sourceLocations?.rowData,
        },
      ]);
      // Don't do the columnTitles check since we don't know what the correct
      // length of the rows is meant to be until this error is fixed.
      return;
    }

    // Ensure that every column is titled, and there aren't excess column titles
    if (columnTitles && columnTitles.length !== (rowData[0]?.length ?? 0)) {
      addErrors([
        {
          message: `The number of titles in columnTitles must match the number of columns in the rows in rowData`,
          ...props._sourceLocations?.columnTitles,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return (
    <Figure>
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 5,
          color: '#34495E',
          breakInside: 'avoid',
        }}
      >
        {title && (
          <caption style={{ fontWeight: semiBoldProximaNova }}>{title}</caption>
        )}
        {columnTitles && (
          <thead>
            <tr>
              {rowTitles && (
                // Transparent is a temporary workaround for CSS style leakage turning
                // th cells blue in Mathspace. Can be removed once these two css files
                // in MathContent get's properly scoped to the component:
                // - table-bootstrap.css
                // - table-flat-ui.css
                <th style={{ backgroundColor: 'transparent' }} />
              )}
              {columnTitles.map((columnTitle, index) => (
                <th
                  key={index}
                  style={{
                    backgroundColor: '#E5E6E8',
                    padding: '2px 10px',
                    fontWeight: 'normal',
                  }}
                >
                  {latexOrText(columnTitlesAs, columnTitle)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rowData.map((row, index) => (
            <tr key={index}>
              {rowTitles && (
                <th
                  style={{
                    backgroundColor: '#E5E6E8',
                    padding: '2px 10px',
                    fontWeight: 'normal',
                  }}
                >
                  {latexOrText(rowTitlesAs, rowTitles[index])}
                </th>
              )}
              {row.map((cell, index) => (
                <td
                  key={index}
                  style={{
                    backgroundColor: '#F1F2F3',
                    padding: '2px 10px',
                    border: 'none',
                  }}
                >
                  {latexOrText(rowDataAs, cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Figure>
  );
};
TabularData.spec = {
  title: t.optional(t.string),
  rowTitles: t.optional(t.arrayOf(t.string)),
  rowTitlesAs: t.optional(t.unionOfValues('latex', 'text')),
  columnTitles: t.optional(t.arrayOf(t.string)),
  columnTitlesAs: t.optional(t.unionOfValues('latex', 'text')),
  rowData: t.arrayOf(t.arrayOf(t.string)),
  rowDataAs: t.unionOfValues('latex', 'text'),
};

export default TabularData;
